/*
  Arrange spices into a gift box right in the cart (drawer and cart page).

  - "Arrange spices in this box" on a gift box added on its own tags that box with a hidden _bb id
    (splitting one off if the line has quantity > 1) and turns on arrange mode.
  - In arrange mode every loose single spice shows "Add to box" (snippets/cart-line-item.liquid). Each tap moves
    ONE jar into the lowest free slot: the jar line gets _bb, _slot and a visible "Custom Box: Box N · Slot k".
  - "×" on a jar in the box sends it back to the cart as a plain single jar.
  - "Edit Box" / "Arrange" on a box row turns arrange mode on for that box; "Done" (or filling the box) turns it off.
  Nothing is added or charged twice: the jars are already in the cart, they're only regrouped.
  The box row itself is snippets/cart-build-box-group.liquid (data-bb-group, data-bb-free, data-bb-name, data-bb-box-key).
*/
(() => {
  // Arrange mode lives only on the current page: leaving the cart (closing the drawer, another page, reload) ends it.
  let current = '';
  const base = window.Shopify?.routes?.root || '/';
  const root = document.documentElement;
  let busy = false;

  const active = () => current;
  let zoomed = false; // magnifier on the box preview; resets whenever arranging ends or moves to another box
  const setActive = (id) => { if ((id || '') !== current) zoomed = false; current = id || ''; apply(); };

  // Reflect the mode on the page: highlight the box being arranged and show the Add to box buttons.
  function apply() {
    const id = active();
    const groups = [...document.querySelectorAll('[data-bb-group]')];
    const live = id && groups.some((g) => g.dataset.bbGroup === id);
    groups.forEach((g) => g.classList.toggle('is-arranging', !!live && g.dataset.bbGroup === id));
    groups.forEach((g) => g.classList.toggle('is-zoomed', !!live && zoomed && g.dataset.bbGroup === id));
    if (live) root.setAttribute('data-bb-arranging', id);
    else root.removeAttribute('data-bb-arranging');
    // The box being arranged left the cart (removed somewhere): leave arrange mode. The cart drawer is on every page.
    if (id && !live && !busy && document.querySelector('s-cart')) { current = ''; zoomed = false; }
  }

  async function post(path, body) {
    const res = await fetch(`${base}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Something went wrong. Please try again.');
    return res.json();
  }
  const getCart = async () => (await fetch(`${base}cart.js`, { credentials: 'same-origin' })).json();
  async function refresh() {
    try {
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch (e) {
      location.reload();
    }
    apply();
  }

  // Keep the gift box line's "Contents" property in step with its slots, for the order.
  async function syncContents(id) {
    const cart = await getCart();
    const lines = (cart.items || []).filter((it) => it.properties?._bb === id);
    const box = lines.find((it) => it.properties?._slot == null);
    if (!box) return lines;
    const contents = lines.filter((it) => it.properties?._slot != null)
      .sort((a, b) => Number(a.properties._slot) - Number(b.properties._slot))
      .map((it) => `${it.properties._slot}. ${it.product_title}`).join(', ');
    const props = { ...box.properties };
    if (contents) props.Contents = contents; else delete props.Contents;
    await post('cart/change.js', { id: box.key, quantity: box.quantity, properties: props });
    return lines;
  }

  // Move jar `a` to slot `b` (and the jar in `b`, if any, to `a`'s slot). a/b = { key, slot }.
  function swapSlots(group, a, b) {
    if (!a.key || a.slot === b.slot) return;
    const id = group.dataset.bbGroup;
    const name = group.dataset.bbName || 'Set';
    run(group, async () => {
      const cart = await getCart();
      const lines = cart.items || [];
      const moveTo = async (key, slot) => {
        const line = lines.find((it) => it.key === key);
        if (!line) return;
        const props = { ...line.properties, 'Custom Box': `${name} · Slot ${slot}`, _bb: id, _slot: String(slot) };
        await post('cart/change.js', { id: key, quantity: line.quantity, properties: props });
      };
      const va = lines.find((it) => it.key === a.key)?.variant_id;
      const vb = b.key ? lines.find((it) => it.key === b.key)?.variant_id : null;
      if (va && va === vb) return; // two jars of the same spice: swapping changes nothing
      await moveTo(a.key, b.slot);
      if (b.key) await moveTo(b.key, a.slot);
      await syncContents(id);
    });
  }

  // ---------- Drag to reorder (box list rows and jars in the small open-box preview) ----------
  // Mouse: drag a jar row (or preview jar) onto another slot. Touch: drag by the grip or a preview jar,
  // so the cart can still scroll when swiping over the rows. Drops swap, or move into an empty slot.
  let drag = null; // { group, item, startX, startY, ghost, over, pointerId }
  let suppressClick = false;
  document.addEventListener('pointerdown', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const item = t?.closest('[data-bb-draggable]');
    if (!item || busy || t.closest('[data-bb-jar-out], [data-bb-change]') || e.button > 0) return;
    const group = item.closest('[data-bb-group]');
    if (!group) return;
    if (!group.classList.contains('is-arranging')) return; // drag only in Edit Box mode (grips hidden otherwise)
    const inPreview = item.classList.contains('bb-mini-slot');
    if (e.pointerType !== 'mouse' && !inPreview && !t.closest('[data-bb-grip]')) return;
    drag = { group, item, startX: e.clientX, startY: e.clientY, ghost: null, over: null, pointerId: e.pointerId };
  });
  document.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    if (!drag.ghost) {
      if (Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 6) return;
      let ghost;
      const host = drag.item.closest('dialog') || document.body; // stay above a modal cart drawer (top layer)
      if (drag.item.tagName === 'LI') {
        // Dragging a list row: the whole row floats under the pointer, held where it was grabbed.
        const r = drag.item.getBoundingClientRect();
        drag.offX = drag.startX - r.left;
        drag.offY = drag.startY - r.top;
        ghost = document.createElement('div');
        ghost.className = 'bb-cart is-arranging bb-row-float';
        ghost.style.width = `${r.width}px`;
        const ol = document.createElement('ol');
        ol.className = drag.item.parentElement?.className || 'bb-cart-list';
        const li = drag.item.cloneNode(true);
        li.classList.remove('is-picked', 'is-drop');
        ol.append(li);
        ghost.append(ol);
        drag.row = true;
      } else {
        ghost = document.createElement('div');
        ghost.className = 'bb-drag-ghost';
        const jar = drag.item.querySelector('.bb-cart-jar, img');
        if (jar) { const c = jar.cloneNode(true); if (c.classList?.contains('bb-cart-jar')) c.style.height = '34px'; else c.style.cssText = 'height:34px;width:auto'; ghost.append(c); }
      }
      host.append(ghost);
      // A transformed drawer shifts position:fixed; measure once and correct.
      ghost.style.left = '0px'; ghost.style.top = '0px';
      const g0 = ghost.getBoundingClientRect();
      drag.fixX = drag.row ? -g0.left : -(g0.left + g0.width / 2); // the jar ghost is centred on the pointer
      drag.fixY = drag.row ? -g0.top : -(g0.top + g0.height / 2);
      drag.ghost = ghost;
      drag.item.classList.add('is-dragging');
    }
    e.preventDefault();
    drag.ghost.style.left = `${e.clientX - (drag.row ? drag.offX : 0) + drag.fixX}px`;
    drag.ghost.style.top = `${e.clientY - (drag.row ? drag.offY : 0) + drag.fixY}px`;
    const under = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-bb-slot-item]');
    const target = under && drag.group.contains(under) && under.dataset.bbSlotItem !== drag.item.dataset.bbSlotItem ? under : null;
    if (target !== drag.over) {
      drag.over?.classList.remove('is-drop');
      target?.classList.add('is-drop');
      drag.over = target;
    }
  }, { passive: false });
  function endDrag(e, cancel) {
    if (!drag || (e && e.pointerId !== drag.pointerId)) return;
    const { group, item, ghost, over } = drag;
    drag = null;
    if (!ghost) return; // it was a tap: the click handler deals with it
    ghost.remove();
    item.classList.remove('is-dragging');
    over?.classList.remove('is-drop');
    suppressClick = true;
    setTimeout(() => { suppressClick = false; }, 0);
    if (cancel || !over) return;
    group.querySelectorAll('.is-picked').forEach((el) => el.classList.remove('is-picked'));
    group.classList.remove('has-pick');
    swapSlots(group, { key: item.dataset.key, slot: item.dataset.bbSlotItem }, { key: over.dataset.key || '', slot: over.dataset.bbSlotItem });
  }
  document.addEventListener('pointerup', (e) => endDrag(e, false));
  document.addEventListener('pointercancel', (e) => endDrag(e, true));
  // A drag ends with a click on the row; don't treat it as a tap.
  document.addEventListener('click', (e) => {
    if (suppressClick) { e.stopImmediatePropagation(); e.preventDefault(); suppressClick = false; }
  }, true);

  async function run(row, task) {
    if (busy) return;
    busy = true;
    row?.setAttribute('aria-busy', 'true');
    try {
      await task();
      await refresh();
    } catch (err) {
      window.alert(err.message);
      row?.removeAttribute('aria-busy');
    } finally {
      busy = false;
    }
  }

  // Images inside boxes must never start the browser's own image drag (it cancels our pointer drag).
  document.addEventListener('dragstart', (e) => {
    if (e.target instanceof Element && e.target.closest('.bb-cart')) e.preventDefault();
  });

  // ---------- Change: swap one jar for another spice (popup: snippets/bb-change-dialog.liquid) ----------
  const changeDialog = document.getElementById('bb-change-dialog');
  let changing = null; // { group, key, slot }
  function showMenu() {
    changeDialog.querySelector('[data-bbc-menu]').hidden = false;
    changeDialog.querySelector('[data-bbc-detail]').hidden = true;
    changeDialog.classList.remove('is-detail');
  }
  function openChange(btn) {
    if (!changeDialog) return;
    const group = btn.closest('[data-bb-group]');
    if (!group) return;
    openChangeFor({ group, key: btn.dataset.bbChange, slot: btn.dataset.slot, variant: btn.dataset.variant });
  }
  function openChangeFor(state) {
    changing = state;
    const sub = changeDialog.querySelector('[data-bbc-sub]');
    if (sub) sub.textContent = (sub.dataset.template || '').replace('%slot%', state.slot);
    changeDialog.querySelectorAll('[data-bbc-tile]').forEach((o) => o.classList.toggle('is-current', o.dataset.bbcTile === String(state.variant)));
    showMenu();
    changeDialog.showModal();
    changeDialog.querySelector('.bbc-grid')?.scrollTo(0, 0);
  }
  // The Build Your Own Box page (assets/build-box.js) uses the same popup: picks there aren't cart lines,
  // so it passes onPick(variantId) and swaps the spice in that slot itself.
  // The Build Your Own Box page (assets/build-box.js) shows a spice's details from its own menu: details view only,
  // Back closes the popup, Choose calls onPick (the builder swaps the spice in the slot being changed).
  window.bbShowInfo = (opts) => {
    if (!changeDialog) return;
    changing = { variant: String(opts.current || ''), onPick: opts.onPick, infoOnly: true };
    const sub = changeDialog.querySelector('[data-bbc-sub]');
    if (sub) sub.textContent = '';
    // Build a Box adding (not changing) relabels the details button "Add"; it goes back to Select on close.
    const pick = changeDialog.querySelector('[data-bbc-detail-pick]');
    if (pick) {
      if (!pick.dataset.defLabel) pick.dataset.defLabel = pick.textContent;
      pick.textContent = opts.pickLabel || pick.dataset.defLabel;
    }
    changeDialog.showModal();
    showInfo(opts.handle);
    if (pick && opts.disabled) pick.disabled = true;
  };
  changeDialog?.addEventListener('close', () => {
    const pick = changeDialog.querySelector('[data-bbc-detail-pick]');
    if (pick?.dataset.defLabel) pick.textContent = pick.dataset.defLabel;
  });
  // Info: load the spice's details into the popup (templates/product.bbinfo.liquid); cached per spice.
  const infoCache = {};
  async function showInfo(handle) {
    const tile = changeDialog.querySelector(`[data-bbc-tile][data-handle="${CSS.escape(handle)}"]`);
    const body = changeDialog.querySelector('[data-bbc-detail-body]');
    const pick = changeDialog.querySelector('[data-bbc-detail-pick]');
    changeDialog.querySelector('[data-bbc-menu]').hidden = true;
    changeDialog.querySelector('[data-bbc-detail]').hidden = false;
    changeDialog.classList.add('is-detail');
    pick.dataset.bbcPick = tile?.dataset.bbcTile || '';
    pick.disabled = tile?.dataset.available !== 'true' || (!!changing?.infoOnly && pick.dataset.bbcPick === changing.variant);
    body.scrollTop = 0;
    if (!infoCache[handle]) {
      body.innerHTML = `<p class="bbc-loading">${body.dataset.loading || ''}</p>`;
      try {
        const res = await fetch(`${base}products/${encodeURIComponent(handle)}?view=bbinfo`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(String(res.status));
        infoCache[handle] = await res.text();
      } catch (err) {
        body.innerHTML = '';
        showMenu();
        return;
      }
    }
    // Still on this spice? (the shopper may have gone back meanwhile)
    if (pick.dataset.bbcPick !== (tile?.dataset.bbcTile || '')) return;
    body.innerHTML = infoCache[handle];
    body.focus({ preventScroll: true });
  }
  if (changeDialog) {
    changeDialog.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t === changeDialog || t.closest('[data-bbc-close]')) { changeDialog.close(); return; }
      if (t.closest('[data-bbc-back]')) { if (changing?.infoOnly) changeDialog.close(); else showMenu(); return; }
      // Details view extras: recipe/review arrows, Read All Reviews, stars jump to Reviews.
      const arrow = t.closest('[data-bbi-scroll]');
      if (arrow) { const track = arrow.parentElement.querySelector('[data-bbi-track]'); if (track) track.scrollBy({ left: Number(arrow.dataset.bbiScroll) * track.clientWidth, behavior: 'smooth' }); return; }
      if (t.closest('[data-bbi-all-reviews]')) { t.closest('.bbi-reviews')?.classList.add('is-all'); return; }
      const go = t.closest('[data-bbi-goto]');
      if (go) { changeDialog.querySelector('#' + go.dataset.bbiGoto)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      // The whole card opens the details (like Build a Box); its Select button picks instead.
      const info = !t.closest('[data-bbc-pick], .bbc-select') && t.closest('[data-bbc-info]');
      if (info) { showInfo(info.dataset.bbcInfo); return; }
      const opt = t.closest('[data-bbc-pick]');
      if (!opt || opt.disabled || !changing || !opt.dataset.bbcPick) return;
      if (changing.onPick) { const cb = changing.onPick; const cur = changing.variant; const v = opt.dataset.bbcPick; changeDialog.close(); if (v !== cur) cb(v); return; }
      if (opt.dataset.bbcPick === changing.variant) { changeDialog.close(); return; }
      const { group, key, slot } = changing;
      const variant = Number(opt.dataset.bbcPick);
      changeDialog.close();
      run(group, async () => {
        const cart = await getCart();
        const line = (cart.items || []).find((it) => it.key === key);
        if (!line) throw new Error('This box changed. Please refresh the page.');
        const name = group.dataset.bbName || 'Set';
        const properties = { ...line.properties, 'Custom Box': `${name} · Slot ${slot}`, _bb: group.dataset.bbGroup, _slot: String(slot) };
        await post('cart/add.js', { items: [{ id: variant, quantity: 1, properties }] });
        await post('cart/change.js', { id: key, quantity: 0 });
        await syncContents(group.dataset.bbGroup);
      });
    });
    changeDialog.addEventListener('close', () => { changing = null; });
  }

  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;

    const changeBtn = t.closest('[data-bb-change]');
    if (changeBtn) { e.preventDefault(); e.stopPropagation(); openChange(changeBtn); return; }

    // Start arranging a gift box that was added on its own.
    const start = t.closest('[data-bb-arrange-start]');
    if (start) {
      e.preventDefault();
      const key = start.dataset.bbArrangeStart;
      const qty = Number(start.dataset.qty) || 1;
      const variant = Number(start.dataset.variant);
      run(start.closest('[style*="view-transition-name"]') || start.parentElement, async () => {
        const cart = await getCart();
        const boxNo = new Set((cart.items || []).map((it) => it.properties?._bb).filter(Boolean)).size + 1;
        const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const props = { 'Custom Box': `Set ${boxNo}`, _bb: id };
        if (qty > 1) {
          await post('cart/change.js', { id: key, quantity: qty - 1 });
          await post('cart/add.js', { items: [{ id: variant, quantity: 1, properties: props }] });
        } else {
          await post('cart/change.js', { id: key, quantity: 1, properties: props });
        }
        current = id;
      });
      return;
    }

    // Edit / Arrange on a box row, and Done.
    const edit = t.closest('[data-bb-arrange-edit]');
    if (edit) { e.preventDefault(); setActive(edit.dataset.bbArrangeEdit); return; }
    if (t.closest('[data-bb-arrange-done]')) { e.preventDefault(); leave(); return; }

    // Add one jar of a loose spice to the box being arranged.
    const add = t.closest('[data-bb-add-to-box]');
    if (add) {
      e.preventDefault();
      const id = active();
      const group = id && [...document.querySelectorAll('[data-bb-group]')].find((g) => g.dataset.bbGroup === id);
      if (!group) { setActive(''); return; }
      const free = (group.dataset.bbFree || '').split(',').filter(Boolean).map(Number);
      if (!free.length) { setActive(''); return; }
      const slot = free[0];
      const name = group.dataset.bbName || 'Set';
      const key = add.dataset.bbAddToBox;
      const qty = Number(add.dataset.qty) || 1;
      const variant = Number(add.dataset.variant);
      run(group, async () => {
        const props = { 'Custom Box': `${name} · Slot ${slot}`, _bb: id, _slot: String(slot), ...(group.hasAttribute('data-bb-nobox') ? { _bbn: group.dataset.bbSize } : {}) };
        if (qty > 1) {
          await post('cart/change.js', { id: key, quantity: qty - 1 });
          await post('cart/add.js', { items: [{ id: variant, quantity: 1, properties: props }] });
        } else {
          await post('cart/change.js', { id: key, quantity: 1, properties: props });
        }
        await syncContents(id);
        if (free.length === 1) current = ''; // that filled the box
      });
      return;
    }

    // Change the order: tap a spice in the box, then tap another spice (they swap) or an empty slot (it moves there).
    const slotItem = t.closest('[data-bb-slot-item]');
    if (slotItem && !t.closest('[data-bb-jar-out]')) {
      const group = slotItem.closest('[data-bb-group]');
      if (!group || !group.classList.contains('is-arranging')) return;
      e.preventDefault();
      const picked = group.querySelector('[data-bb-slot-item].is-picked');
      const clearPick = () => {
        group.querySelectorAll('.is-picked').forEach((el) => el.classList.remove('is-picked'));
        group.classList.remove('has-pick');
      };
      if (!picked) {
        if (!slotItem.dataset.key) return; // nothing to move from an empty slot
        slotItem.classList.add('is-picked');
        group.classList.add('has-pick');
        return;
      }
      if (picked === slotItem) { clearPick(); return; }
      const a = { key: picked.dataset.key, slot: picked.dataset.bbSlotItem };
      const b = { key: slotItem.dataset.key || '', slot: slotItem.dataset.bbSlotItem };
      clearPick();
      swapSlots(group, a, b);
      return;
    }

    // Take a jar out of the box: it goes back to the cart as a plain single jar.
    const out = t.closest('[data-bb-jar-out]');
    if (out) {
      e.preventDefault();
      const group = out.closest('[data-bb-group]');
      const id = group?.dataset.bbGroup;
      run(group, async () => {
        await post('cart/change.js', { id: out.dataset.bbJarOut, quantity: 0 });
        await post('cart/add.js', { items: [{ id: Number(out.dataset.variant), quantity: 1 }] });
        if (id) await syncContents(id);
      });
    }
  });

  // Leaving arrange mode (Done, closing the cart drawer, or coming back to the page later): a box that never got
  // any spices goes back to a plain gift box with just "Arrange spices in this box".
  async function revertEmpty() {
    const empties = [...document.querySelectorAll('[data-bb-group]')].filter((g) => {
      const free = (g.dataset.bbFree || '').split(',').filter(Boolean).length;
      return g.dataset.bbBoxKey && free > 0 && free === Number(g.dataset.bbSize);
    });
    const boxes = new Map();
    empties.forEach((g) => boxes.set(g.dataset.bbBoxKey, Number(g.dataset.bbBoxVariant)));
    if (!boxes.size || busy) return;
    busy = true;
    try {
      // Swap the tagged box line for a plain one (same box, no Custom Box label), merging with any plain box already there.
      for (const [key, variant] of boxes) {
        await post('cart/change.js', { id: key, quantity: 0 });
        if (variant) await post('cart/add.js', { items: [{ id: variant, quantity: 1 }] });
      }
      await refresh();
    } catch (err) {
      /* leave it as an empty box; the customer can still arrange or remove it */
    } finally {
      busy = false;
    }
  }
  function leave() {
    setActive('');
    revertEmpty();
  }
  // Zoom: the magnifier on the box preview toggles a large preview (still draggable) while arranging.
  document.addEventListener('click', (e) => {
    const z = e.target instanceof Element ? e.target.closest('[data-bb-zoom]') : null;
    if (!z) return;
    e.preventDefault();
    zoomed = !zoomed;
    apply();
    if (zoomed) z.closest('.bb-mini-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
  // No long-press menu (Save image / Copy / Open) on the preview or jar photos while arranging.
  document.addEventListener('contextmenu', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (t && (t.closest('.bb-mini') || t.closest('.bb-cart.is-arranging img'))) e.preventDefault();
  });
  // The cart drawer closing ends arranging ('close' doesn't bubble, so listen in the capture phase).
  document.addEventListener('close', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target.id === 'cart-drawer' && current) leave();
  }, true);

  // Carts re-render after every change: re-apply the mode each time.
  new MutationObserver(() => apply()).observe(document.body, { childList: true, subtree: true });
  apply();
  // Arriving on any page (incl. back to the cart): never in arrange mode, and empty boxes go back to plain.
  revertEmpty();
})();
